<template>
  <div class="ThePublisherDashboard">
    <v-row class="ThePublisherDashboard__row">
      <v-col cols="18">
        <publisher-dashboard-section-wrapper title="Value Metrics">
          <publisher-dashboard-metrics
            :pluginInstallsCount="valueMetrics.pluginInstallsCount"
            :publishedPluginsCount="valueMetrics.publishedPluginsCount"
            :developersCount="valueMetrics.developersCount"
            :sdkVersion="valueMetrics.sdkVersion"
          />
        </publisher-dashboard-section-wrapper>
      </v-col>
      <v-col cols="18">
        <publisher-dashboard-section-wrapper title="Plugin Status">
          <publisher-plugin-status
            v-if="statisticsChartData"
            :chartData="statisticsChartData"
          />
        </publisher-dashboard-section-wrapper>
      </v-col>
    </v-row>
    <v-row class="ThePublisherDashboard__row">
      <v-col>
        <publisher-dashboard-developer-map
          :statistics="developersStatistics"
          v-if="developersStatistics"
        />
      </v-col>
      <v-col>
        <publisher-overall-installation />
      </v-col>
    </v-row>
    <publisher-dashboard-top-plugins />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import marketplaceAPI from '@/services/marketplace'

import PublisherDashboardSectionWrapper from './ThePublisher/Dashboard/PublisherDashboardSectionWrapper.vue'
import PublisherDashboardMetrics from './ThePublisher/Dashboard/PublisherDashboardMetrics.vue'
import PublisherPluginStatus from './ThePublisher/Dashboard/PublisherPluginStatus.vue'
import PublisherOverallInstallation from './ThePublisher/Dashboard/PublisherOverallInstallation.vue'
import PublisherDashboardTopPlugins from './ThePublisher/Dashboard/PublisherDashboardTopPlugins.vue'
import PublisherDashboardDeveloperMap from './ThePublisher/Dashboard/PublisherDashboardDeveloperMap.vue'

export default {
  name: 'ThePublisherDashboard',
  components: {
    PublisherDashboardSectionWrapper,
    PublisherDashboardTopPlugins,
    PublisherPluginStatus,
    PublisherOverallInstallation,
    PublisherDashboardMetrics,
    PublisherDashboardDeveloperMap,
  },
  data() {
    return {
      valueMetrics: {
        pluginInstallsCount: 0,
        publishedPluginsCount: 0,
        developersCount: 0,
        sdkVersion: '',
      },
      developersStatistics: null,
      statisticsChartData: null,
    }
  },
  computed: {
    ...mapState({
      currentPublisherInfo: ({ publisher_infos }) => publisher_infos.current,
    }),
  },
  async mounted() {
    await this.getPluginStatistics()
    await this.getDevelopersStatistics()
    await this.getLatestSDKStatistics()
  },
  methods: {
    async getPluginStatistics() {
      try {
        // Get all plugins (no status filter to get all statuses)
        const plugins = await marketplaceAPI.listPlugins({})

        // Map marketplace status to legacy status format
        const pluginsMap = plugins.reduce(
          (acc, plugin) => {
            let key = 'draft'
            const status = (plugin.status || '').toLowerCase()
            if (status === 'active') key = 'ready-for-sale'
            if (status === 'draft') key = 'draft'
            // Note: Marketplace API may not have "in-review" status directly
            // You may need to check plugin version status or add a review status field
            return { ...acc, [key]: [...acc[key], plugin] }
          },
          {
            'ready-for-sale': [],
            'in-review': [],
            draft: [],
          }
        )

        // Try to get plugin status from analytics, otherwise use plugin list
        try {
          const analyticsResponse = await marketplaceAPI.getInstallationAnalytics({})
          const analytics = analyticsResponse.analytics || analyticsResponse
          
          if (analytics.plugin_status_overview && analytics.plugin_status_overview.length > 0) {
            // Use API status types directly - component will handle mapping
            this.statisticsChartData = analytics.plugin_status_overview.map((item) => ({
              status: item.status, // Use API status type directly
              slug: item.status,   // Also set slug for compatibility
              count: item.count || 0,
              value: item.count || 0, // Component expects 'value' field
            }))
          } else {
            // Fallback to plugin list mapping - convert to API status format
            this.statisticsChartData = [
              { status: 'published', slug: 'published', value: pluginsMap['ready-for-sale'].length },
              { status: 'in-review', slug: 'in-review', value: pluginsMap['in-review'].length },
              { status: 'draft', slug: 'draft', value: pluginsMap['draft'].length },
            ]
          }
        } catch (error) {
          console.warn('Could not fetch plugin status from analytics, using plugin list:', error)
          // Fallback to plugin list mapping - convert to API status format
          this.statisticsChartData = [
            { status: 'published', slug: 'published', value: pluginsMap['ready-for-sale'].length },
            { status: 'in-review', slug: 'in-review', value: pluginsMap['in-review'].length },
            { status: 'draft', slug: 'draft', value: pluginsMap['draft'].length },
          ]
        }

        // Get installation count and metrics from installations analytics
        try {
          const analyticsResponse = await marketplaceAPI.getInstallationAnalytics({})
          const analytics = analyticsResponse.analytics || analyticsResponse
          
          // Use plugin_metrics from analytics if available, otherwise use direct values
          if (analytics.plugin_metrics) {
            this.valueMetrics.pluginInstallsCount = analytics.plugin_metrics.total_installs || analytics.active_installations || 0
            this.valueMetrics.publishedPluginsCount = analytics.plugin_metrics.total_published_plugins || 0
            this.valueMetrics.developersCount = analytics.plugin_metrics.total_developers || 0
          } else {
            // Fallback to direct analytics values
            this.valueMetrics.pluginInstallsCount = analytics.active_installations || analytics.total_installations || 0
            this.valueMetrics.publishedPluginsCount = pluginsMap['ready-for-sale'].length
          }
        } catch (error) {
          console.warn('Could not fetch installation analytics, using plugin count:', error)
          // Fallback: count installations manually
          const installations = await marketplaceAPI.listInstallations({ status: 'active' })
          this.valueMetrics.pluginInstallsCount = installations.length
          this.valueMetrics.publishedPluginsCount = pluginsMap['ready-for-sale'].length
        }
      } catch (error) {
        console.error('Error fetching plugin statistics:', error)
      }
    },
    async getDevelopersStatistics() {
      try {
        // Try to get developers count from analytics first
        try {
          const analyticsResponse = await marketplaceAPI.getInstallationAnalytics({})
          const analytics = analyticsResponse.analytics || analyticsResponse
          if (analytics.plugin_metrics?.total_developers) {
            this.valueMetrics.developersCount = analytics.plugin_metrics.total_developers
          }
        } catch (error) {
          console.warn('Could not get developers count from analytics:', error)
        }
        
        // Fetch developers for country statistics
        const developers = await marketplaceAPI.listDevelopers({})
        // Only update count if not already set from analytics
        if (!this.valueMetrics.developersCount) {
          this.valueMetrics.developersCount = developers.length
        }
        
        // Get developers by country from analytics if available
        const analyticsResponse = await marketplaceAPI.getInstallationAnalytics({})
        const analytics = analyticsResponse.analytics || analyticsResponse
        if (analytics.developers_by_country && analytics.developers_by_country.length > 0) {
          // Map analytics format to component format
          const statistics = analytics.developers_by_country.reduce((acc, item) => {
            const countryCode = item.country_code || item.country
            if (!countryCode) return acc
            return {
              ...acc,
              [countryCode]: {
                developers: item.developers_count || item.count || 0,
              },
            }
          }, {})
          this.developersStatistics = statistics
        } else {
          // Fallback to calculating from developers list
          const statistics = developers.reduce((acc, cur) => {
            const countryCode = cur.country
            if (!countryCode) return acc
            return {
              ...acc,
              [countryCode]: {
                developers: acc[countryCode]
                  ? acc[countryCode].developers + 1
                  : 1,
              },
            }
          }, {})
          this.developersStatistics = statistics
        }
      } catch (error) {
        console.error('Error fetching developers statistics:', error)
      }
    },
    async getLatestSDKStatistics() {
      try {
        // Note: SDK version endpoint may not exist in marketplace API
        // This might need to be fetched from a different source or removed
        // For now, we'll leave it empty or fetch from a different endpoint if available
        this.valueMetrics.sdkVersion = ''
      } catch (error) {
        console.error('Error fetching SDK statistics:', error)
      }
    },
  },
}
</script>
<style lang="scss">
$style: ThePublisherDashboard;
.#{$style} {
  padding: 42px 24px 24px;
  &__row {
    margin-bottom: 58px;
  }
  @media screen and (max-width: 600px) {
    &__row {
      margin-bottom: 0px;
    }
  }
}
.w-full {
  width: 100%;
}
</style>
